import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  #accountMenuCleanup = null;
  #toastTimeout = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#setupAccountMenuContinueShoppingClose();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#accountMenuCleanup?.();
    this.#accountMenuCleanup = null;
    if (this.#toastTimeout) {
      clearTimeout(this.#toastTimeout);
      this.#toastTimeout = null;
    }
  }

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount !== undefined) {
      this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
    }

    this.#showAddToCartToast(event);
  };

  #setupAccountMenuContinueShoppingClose() {
    const accountElement = this.querySelector('shopify-account');
    const shadowRoot = accountElement?.shadowRoot;
    if (!shadowRoot) return;

    const handleClick = (event) => {
      const trigger = event
        .composedPath()
        .find((node) => node instanceof HTMLElement && node.closest('button, a'));

      if (!(trigger instanceof HTMLElement)) return;

      const label = trigger.textContent?.trim().toLowerCase();
      if (label !== 'continue shopping') return;

      event.preventDefault();
      event.stopPropagation();

      shadowRoot.querySelector('[aria-label="Close menu"]')?.click();
    };

    shadowRoot.addEventListener('click', handleClick, true);
    this.#accountMenuCleanup = () => shadowRoot.removeEventListener('click', handleClick, true);
  }

  #showAddToCartToast(event) {
    const source = event.detail?.data?.source;
    const didError = event.detail?.data?.didError;
    const itemCount = Number(event.detail?.data?.itemCount) || 0;

    if (
      didError ||
      (source !== 'product-form-component' && source !== 'product-form-component:success') ||
      itemCount <= 0
    ) {
      return;
    }

    const toast = this.querySelector('.header-actions__cart-toast');
    const toastText = this.querySelector('.header-actions__cart-toast-text');

    if (!(toast instanceof HTMLElement) || !(toastText instanceof HTMLElement)) return;

    const oneItemText = Theme?.translations?.items_added_to_cart_one || '1 item added to cart';
    const itemsText = Theme?.translations?.items_added_to_cart_other || '{{ count }} items added to cart';
    const message = itemCount === 1 ? oneItemText : itemsText.replace('{{ count }}', itemCount.toString());

    this.#revealHeaderForToast();
    toastText.textContent = message;
    toast.classList.add('is-visible');

    if (this.#toastTimeout) {
      clearTimeout(this.#toastTimeout);
    }

    this.#toastTimeout = setTimeout(() => {
      toast.classList.remove('is-visible');
      this.#toastTimeout = null;
    }, 2400);
  }

  #revealHeaderForToast() {
    const header = document.querySelector('#header-component');
    if (!(header instanceof HTMLElement)) return;

    const stickyMode = header.getAttribute('sticky');
    if (!stickyMode) return;

    if (header.dataset.stickyState === 'idle') {
      header.dataset.stickyState = 'active';
    }

    if (stickyMode === 'scroll-up' || stickyMode === 'always') {
      header.dataset.scrollDirection = 'up';
    }
  }
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
